/**
 * Landing v3 — the phone and tablet-portrait layout (below `lg`, user
 * 2026-09-28): the desktop scene's story in the same order, rebuilt for a
 * narrow touch screen instead of squeezed onto it.
 *
 * - The first interaction is the Figma frames' (116:885 → 116:972 → 116:1100,
 *   landing-phone-hero): the hero pinned while its chips drift, then organize
 *   into the rail beside the first card as it rises, Ka gliding in to speak
 *   his line. The chrome is the desktop's: a fixed top bar that opens on the
 *   full logo (landing-phone-bar), and the floating button with Ka beside it.
 * - The ten feature cards are ONE pile, as the desktop deck is (user
 *   2026-09-29): each card pins under the top bar and the next slides up over
 *   it, the covered one sinking into the pile the way the desktop's
 *   under-cards sit, the rail lighting the card on top. The pinning is the
 *   browser's own (`position: sticky`); only the sinking follows the scroll.
 * - Everything else simply rises in once as it arrives, and the page scrolls
 *   natively.
 * - Every interaction is a tap: the chips jump to their card, the demos flip,
 *   rate, answer and seek. Nothing takes a drag — a drag on a phone is the
 *   reader scrolling — so the mind map and the occlusion editor play their
 *   intro and then sit still, and the notes are read rather than typed in.
 * - The rows that drift on desktop (reviews, posts) are rows the reader swipes.
 * - Ka is one floating Ka until the close, where the desk Ka takes over.
 * - Cheap to scroll: no animation-frame loops, anything continuous paused off
 *   screen, demos mounted only near the top of the pile, and no backdrop blur
 *   on the large surfaces.
 */
import {
  motion,
  type MotionValue,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from 'framer-motion';
import {
  Fragment,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { FALLBACK_USER_COUNT, formatCompactMillions } from '@/hooks/use-live-user-count';
import { cn } from '@/lib/utils';

import { AgentChat } from './landing-agent-chat';
import {
  clamp01,
  GetStartedCta,
  GLASS_PILL_CLASS,
  PageLattice,
  sectionAnchor,
  StoreRatingPills,
  useLandingCardArrival,
  useScrollTravel,
} from './landing-chrome';
import { FaqSection } from './landing-faq';
import {
  LANDING_FEATURES,
  LANDING_GROUPS,
  type LandingFeature,
} from './landing-features';
import { LandingFooter } from './landing-footer';
import { FloatingGetApp, FloatingSignUp } from './landing-get-app';
import { WandIcon } from './landing-icons';
import { whenLandingSettled } from './landing-idle';
import {
  CARDS_WITH_KA,
  DESK_KA_HEADROOM,
  DeskKa,
  EASE_OUT,
  KA_SMALL,
  KaSlot,
  setKaInCard,
} from './landing-ka';
import { TriggeredLine } from './landing-lines';
import { WorldDotMap } from './landing-map';
import { PHONE_BAR_HEIGHT, PHONE_BAR_TOP, PhoneTopBar } from './landing-phone-bar';
import {
  heroKaSpot,
  NO_RAIL,
  PhoneChipLayer,
  PhoneHeroStage,
  type RailGeometry,
  useHeroBox,
  useHeroTimeline,
} from './landing-phone-hero';
import { PhoneKa } from './landing-phone-ka';
import {
  pileCover,
  pileDepth,
  type PileGeometry,
  pileRestAt,
  stageFit,
} from './landing-phone-layout';
import { PostsRow, TikTokButton } from './landing-posts';
import { ReviewsRow } from './landing-reviews';
import { useStoreDevice } from './landing-store';
import { TilesSection } from './landing-tiles';
import {
  FeatureMedia,
  hasFeatureMedia,
  type MediaPhase,
  preloadFeatureMedia,
} from './media/feature-media';

/** A block's headline on a phone: the desktop's two-tone D2, a size down. */
const PHONE_HEADLINE_CLASS =
  'text-center text-[30px] font-bold leading-[38px] text-secondary-800';

/** The one-off rise every block makes as it arrives. */
function useRise(reduceMotion: boolean, amount = 0.2) {
  const ref = useRef<HTMLElement>(null);
  const arrived = useInView(ref, { once: true, amount }) || reduceMotion;
  const rise = {
    initial: reduceMotion ? false : { opacity: 0, y: 24 },
    animate: arrived ? { opacity: 1, y: 0 } : undefined,
    transition: { duration: 0.6, ease: EASE_OUT },
  } as const;
  return { ref, arrived, rise };
}

// ── The features: one pile ───────────────────────────────────────────────────

/** The pile's edges, laid out as the desktop deck's under-cards (DeckCard):
 *  each card under the top one sits 8px lower and 16px narrower, and a card
 *  more than two and a half deep is gone. */
const PILE_DROP = 8;
const PILE_NARROW = 16;
/** Scroll between one card pinning and the next starting over it: the beat
 *  the top card sits alone. */
const PILE_DWELL = 32;
/** Where a pinned card's top rests on the frames' screen: 40px under the top
 *  bar (Figma 116:1100; `.lv3-phone` tightens it on shorter screens). */
const PILE_TOP = PHONE_BAR_TOP + PHONE_BAR_HEIGHT + 40;
/** The rail's height: its three pills of 36px chips, 6px apart, 8px between
 *  pills (Figma 116:1107). The rail sits centred on the card beside it. */
const RAIL_HEIGHT = 448;

/** A card's surface: OPAQUE, so the cards under it never show through, and,
 *  like every large surface here, without backdrop blur. */
const PILE_CARD_CLASS = 'border border-secondary-200 bg-secondary-50 shadow-card-row';

const NO_PILE: PileGeometry = { tops: [], rest: PILE_TOP, height: 0, width: 0 };

/** An element's top in the document, by layout — an entrance's lift, or a
 *  card pinned by `sticky`, doesn't move it. */
function layoutTop(el: HTMLElement): number {
  let top = 0;
  for (
    let node: HTMLElement | null = el;
    node;
    node = node.offsetParent as HTMLElement | null
  ) {
    top += node.offsetTop;
  }
  return top;
}

/**
 * Each demo's STAGE on a phone (user 2026-09-29, Figma 116:1267): the size it
 * is laid out at — its essential piece only (the flashcard alone, the
 * transcriber's player, the note's text) — before the card scales it down,
 * whole, to fit and centres it. So every demo is a miniature of itself at
 * much the same scale, nothing is cut off, and no well frames it: it sits on
 * the card as the frame's flashcard does. A stage without a height takes its
 * content's own. On a short screen, a demo that fills its box (`minH`) gets a
 * shorter one rather than shrink under `STAGE_MIN_SCALE`.
 */
const PHONE_STAGES: Record<string, { w: number; h?: number; minH?: number }> = {
  // Figma 116:1267's card; the study card matches it.
  flashcards: { w: 360, h: 466, minH: 300 },
  spacedRepetition: { w: 360, h: 466, minH: 340 },
  // The map to its branches is wider than tall.
  mindMaps: { w: 420, h: 240 },
  transcriber: { w: 400 },
  summaries: { w: 400, h: 500, minH: 300 },
  imageOcclusion: { w: 440, h: 440 * (576 / 740) },
  examPlanner: { w: 400 },
  // Whole, always: the longest question (German) and its four choices.
  quizzes: { w: 420, h: 580 },
  // Ka, his bubble over him and the exam's pill under him, whole.
  oralExam: { w: 400, h: 560, minH: 460 },
  // The case over its answer bar, then the report, in the same box.
  caseStudies: { w: 420, h: 600, minH: 560 },
};
function PileWell({
  featureId,
  live,
  children,
}: {
  featureId: string;
  live: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const stage = PHONE_STAGES[featureId];
  const [fit, setFit] = useState<ReturnType<typeof stageFit> | null>(null);
  useLayoutEffect(() => {
    const well = ref.current;
    const staged = stageRef.current;
    if (!well || !staged || !stage) return;
    const measure = () => {
      const w = well.clientWidth;
      const h = well.clientHeight;
      // A stage without a height is as tall as its content, laid out.
      const sh = stage.h ?? staged.offsetHeight;
      if (!w || !h || !sh) return;
      const next = stageFit({ ...stage, h: sh }, { w, h });
      setFit((prev) =>
        prev &&
        prev.x === next.x &&
        prev.y === next.y &&
        prev.h === next.h &&
        prev.scale === next.scale
          ? prev
          : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(well);
    observer.observe(staged);
    return () => observer.disconnect();
  }, [stage]);
  return (
    <div ref={ref} inert={!live} className="lv3-pile-well relative min-h-0 w-full flex-1">
      {stage ? (
        <div
          ref={stageRef}
          className="absolute left-0 top-0 origin-top-left"
          style={{
            width: stage.w,
            height: stage.h === undefined ? undefined : (fit?.h ?? stage.h),
            transform: fit
              ? `translate(${fit.x}px, ${fit.y}px) scale(${fit.scale})`
              : undefined,
            // Hidden until placed: never a full-size flash.
            visibility: fit ? undefined : 'hidden',
          }}>
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * One feature, as a card with its REAL demo. The demo is mounted only while
 * its card is coming up, on top, or just under the top card, and it is live —
 * taking taps, playing — only on top. It runs `still`: nothing in it takes a
 * drag. Under reduced motion nothing pins or sinks: the cards are a plain
 * list, each live while it holds the middle of the screen.
 */
function PileCard({
  feature,
  index,
  geometry,
  layoutTick,
  scrollY,
  reduceMotion,
}: {
  feature: LandingFeature;
  index: number;
  geometry: RefObject<PileGeometry>;
  layoutTick: MotionValue<number>;
  scrollY: MotionValue<number>;
  reduceMotion: boolean;
}) {
  const { t } = useTranslation();
  const depth = () => {
    layoutTick.get();
    return reduceMotion
      ? 0
      : Math.min(4, pileDepth(geometry.current, index, scrollY.get()));
  };
  const y = useTransform(() => PILE_DROP * depth());
  const scale = useTransform(
    () => 1 - (PILE_NARROW / (geometry.current.width || 358)) * depth(),
  );
  const opacity = useTransform(() => 1 - clamp01((depth() - 2.5) / 1.2));
  const visibility = useTransform(() => (opacity.get() <= 0.01 ? 'hidden' : 'visible'));
  // Its copy leaves as it is covered: the pile shows edges, never ghosts.
  const contentOpacity = useTransform(() => 1 - clamp01((depth() - 0.3) / 0.6));
  // Only a sinking card needs its own layer; the top one re-rasters crisp.
  const willChange = useTransform(() => (depth() > 0.001 ? 'transform' : 'auto'));

  const phaseNow = (): MediaPhase => {
    const g = geometry.current;
    const sy = scrollY.get();
    const vh = window.innerHeight;
    // The card's top on screen as the flow has it (pinned, it stays at `rest`).
    const top = (g.tops[index] ?? 0) - sy;
    if (reduceMotion) {
      if (top <= vh / 2 && top + g.height >= vh / 2) return 'live';
      return top < vh * 1.5 && top + g.height > -vh / 2 ? 'warm' : 'idle';
    }
    const d = pileDepth(g, index, sy);
    if (d >= 1.6) return 'idle';
    // Being covered: still in sight, no longer the reader's.
    if (d >= 0.1) return 'leaving';
    // On top, or most of the way there.
    const arrived =
      index === 0 ? top <= g.rest + g.height * 0.4 : pileCover(g, index, sy) >= 0.6;
    if (arrived) return 'live';
    // Half of it on screen: a build-in canvas starts drawing in sight.
    if (top + g.height / 2 <= vh) return 'revealing';
    return top < vh * 1.5 ? 'warm' : 'idle';
  };
  const [phase, setPhase] = useState<MediaPhase>('idle');
  const [covered, setCovered] = useState(false);
  const settledRef = useRef(false);
  const update = () => {
    const g = geometry.current;
    if (!g.tops.length) return;
    // A couple of screens ahead: fetch the chunk, so mounting never waits —
    // once the page has settled (or the reader has started scrolling): on
    // the first view these chunks would compete with the hero itself.
    if (
      (settledRef.current || scrollY.get() > 0) &&
      (g.tops[index] ?? 0) - scrollY.get() < window.innerHeight * 2.5
    ) {
      preloadFeatureMedia(feature.id);
    }
    const next = phaseNow();
    setPhase((prev) => (prev === next ? prev : next));
    const under = !reduceMotion && pileDepth(g, index, scrollY.get()) >= 0.5;
    setCovered((prev) => (prev === under ? prev : under));
    if (CARDS_WITH_KA.has(feature.id)) setKaInCard(feature.id, kaShownHere());
  };
  useMotionValueEvent(scrollY, 'change', update);
  useMotionValueEvent(layoutTick, 'change', update);
  useEffect(() => {
    let live = true;
    void whenLandingSettled().then(() => {
      if (!live) return;
      settledRef.current = true;
      update();
    });
    return () => {
      live = false;
    };
    // `update` reads refs and motion values only; once is enough.
  }, []);
  // This card draws Ka himself (the oral exam): from the moment it rises
  // onto the screen until its copy has faded under the next card, the
  // floating Ka steps out — never two Kas at once.
  const kaShownHere = () => {
    const g = geometry.current;
    const sy = scrollY.get();
    const top = (g.tops[index] ?? 0) - sy;
    const onScreen = top < window.innerHeight && top + g.height > 0;
    return onScreen && (reduceMotion || pileDepth(g, index, sy) < 0.9);
  };
  useEffect(() => {
    if (!CARDS_WITH_KA.has(feature.id)) return;
    return () => setKaInCard(feature.id, false);
  }, [feature.id]);

  return (
    <motion.article
      id={`lv3-feature-${feature.id}`}
      data-lv3-pile-card=""
      // A covered card's CTA and demo are out of reach: Tab never lands under
      // the pile.
      inert={covered}
      style={{ y, scale, opacity, visibility, willChange, transformOrigin: '50% 100%' }}
      className={cn(
        'lv3-pile-card flex flex-col overflow-clip rounded-[32px] p-4',
        !reduceMotion && 'lv3-pile-card--pinned',
        PILE_CARD_CLASS,
      )}>
      {/* Figma 116:1101 (user 2026-09-29): the badge, the demo, then the
          title and the line under it — no Try it now on a phone; the demo is
          the try. */}
      <motion.div
        style={{ opacity: contentOpacity }}
        className="flex min-h-0 flex-1 flex-col gap-2">
        <span
          className={cn(
            'inline-flex h-9 shrink-0 items-center self-start rounded-[12px] border px-2 text-[14px] font-bold leading-[18px]',
            feature.badgeClass,
          )}>
          {t(feature.badgeKey)}
        </span>
        {/* Everything the copy leaves of the card: the demo, a miniature
            centred in it (`PHONE_STAGES`). */}
        <PileWell featureId={feature.id} live={phase === 'live'}>
          {hasFeatureMedia(feature.id) ? (
            <FeatureMedia featureId={feature.id} phase={phase} still compact />
          ) : (
            // No demo yet (oral exam, case studies): the feature's own chip,
            // bobbing while its card is on top.
            <span className="flex h-full w-full items-center justify-center">
              <span
                style={{
                  animationDuration: '3.6s',
                  animationPlayState: phase === 'live' ? 'running' : 'paused',
                }}
                className={cn(
                  'lv3-chip-float flex h-20 w-20 items-center justify-center rounded-[26px] border-2',
                  feature.chipClass,
                  feature.shadowClass,
                )}>
                <feature.icon className={cn('h-11 w-11', feature.iconClass)} />
              </span>
            </span>
          )}
        </PileWell>
        <div className="flex shrink-0 flex-col gap-1 p-1">
          <h2 className="text-[24px] font-bold leading-8 text-secondary-800">
            {t(feature.titleKey)}
          </h2>
          <p className="text-[16px] font-semibold leading-[22px] text-fg-subtle">
            {t(feature.subtitleKey)}
          </p>
        </div>
      </motion.div>
    </motion.article>
  );
}

/**
 * The rail beside the pile (Figma 116:1107): the three groups as glass pills
 * of the features' chips. The card on top has its chip lit, the rest at 30%;
 * a tap lands on that card. The hero's chips fly into these slots (the rail's
 * own chips appear as they land, `reveal`), and the rail stays pinned beside
 * the pile until the pile leaves.
 */
function PileRail({
  railRef,
  active,
  reveal,
  onOpen,
}: {
  railRef: RefObject<HTMLDivElement | null>;
  active: number;
  reveal: MotionValue<number>;
  onOpen: (index: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <nav
      ref={railRef}
      aria-label={t('landingV3.nav.featuresNav')}
      className="lv3-pile-rail flex flex-col gap-2">
      {LANDING_GROUPS.map((group) => (
        <div key={group.id} className={cn('rounded-[16px] p-1', GLASS_PILL_CLASS)}>
          <motion.div style={{ opacity: reveal }} className="flex flex-col gap-1.5">
            {group.features.map((feature) => {
              const index = LANDING_FEATURES.indexOf(feature);
              return (
                <button
                  key={feature.id}
                  type="button"
                  data-lv3-rail-slot={index}
                  onClick={() => onOpen(index)}
                  aria-label={t(feature.badgeKey)}
                  aria-current={index === active || undefined}
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-[11.5px] border transition-opacity duration-200',
                    feature.chipClass,
                    feature.shadowClass,
                    index === active ? 'opacity-100' : 'opacity-30',
                  )}>
                  <feature.icon className={cn('h-5 w-5', feature.iconClass)} />
                </button>
              );
            })}
          </motion.div>
        </div>
      ))}
    </nav>
  );
}

/**
 * The ten features as ONE pile, the desktop deck's (user 2026-09-29): every
 * card pins under the top bar, and the next one slides up over it. The cards
 * are siblings in one column, so each can stay pinned until the last has
 * landed; then the whole pile leaves together, the rail with it. It is pulled
 * up under the pinned hero, so the first card rises over the hero.
 */
function PhonePile({
  reduceMotion,
  scrollY,
  geometry,
  rail,
  layoutTick,
  railReveal,
  onOpen,
}: {
  reduceMotion: boolean;
  scrollY: MotionValue<number>;
  /** Filled in here; the page's jumps to a card read it. */
  geometry: RefObject<PileGeometry>;
  /** The rail's slots, filled in here for the hero's chips to land on. */
  rail: RefObject<RailGeometry>;
  /** Bumped after every measure. */
  layoutTick: MotionValue<number>;
  /** The rail's own chips, shown as the hero's land on them. */
  railReveal: MotionValue<number>;
  onOpen: (index: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const markers = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => {
      const card = container.querySelector<HTMLElement>('[data-lv3-pile-card]');
      const rest = card ? parseFloat(getComputedStyle(card).top) : NaN;
      const tops = markers.current.map((m) => (m ? layoutTop(m) + m.offsetHeight : 0));
      const height = card?.offsetHeight ?? 0;
      geometry.current = {
        // Each card starts where its marker ends.
        tops,
        rest: Number.isFinite(rest) ? rest : PILE_TOP,
        height,
        width: card?.offsetWidth ?? 0,
      };
      const railEl = railRef.current;
      if (railEl) {
        // A card shorter than the rail gets it scaled to its height.
        const railScale = height > 0 ? Math.min(1, height / RAIL_HEIGHT) : 1;
        railEl.style.transform = railScale < 1 ? `scale(${railScale})` : '';
        rail.current = {
          scale: railScale,
          // Its drawn left edge (a scaled rail is narrower, centred).
          left: railEl.getBoundingClientRect().left + window.scrollX,
          // Its place in the flow, centred on the first card: a pinned rail's
          // own offset would report where it is pinned.
          top: (tops[0] ?? 0) + Math.max(0, (height - RAIL_HEIGHT) / 2),
          // Relative to the rail by their boxes: each glass pill (its
          // backdrop blur) is its own slots' offset parent.
          slots: (() => {
            const box = railEl.getBoundingClientRect();
            return [...railEl.querySelectorAll<HTMLElement>('[data-lv3-rail-slot]')].map(
              (slot) => {
                const r = slot.getBoundingClientRect();
                return {
                  x: r.left - box.left + r.width / 2,
                  y: r.top - box.top + r.height / 2,
                };
              },
            );
          })(),
        };
      }
      layoutTick.set(layoutTick.get() + 1);
    };
    measure();
    // Anything above the pile moving it (a font, a block laying out), or the
    // screen changing size.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [geometry, rail, layoutTick]);

  // The card on top, lit in the rail.
  const [active, setActive] = useState(0);
  const updateActive = () => {
    const g = geometry.current;
    const y = scrollY.get();
    let next = 0;
    if (reduceMotion) {
      const mid = y + window.innerHeight / 2;
      g.tops.forEach((top, i) => {
        if (top <= mid) next = i;
      });
    } else {
      for (let k = 1; k < g.tops.length; k += 1) if (pileCover(g, k, y) >= 0.5) next = k;
    }
    setActive((prev) => (prev === next ? prev : next));
  };
  useMotionValueEvent(scrollY, 'change', updateActive);
  useMotionValueEvent(layoutTick, 'change', updateActive);

  return (
    <div
      ref={containerRef}
      className="lv3-pile lv3-pile-after-hero grid w-full max-w-[640px] grid-cols-[minmax(0,1fr)_46px] gap-x-2 px-3">
      <div>
        {LANDING_FEATURES.map((feature, i) => (
          <Fragment key={feature.id}>
            {/* The dwell before each card (spacers, not margins: a pinned
                card's margin would push the pile out early). */}
            <div
              ref={(el) => {
                markers.current[i] = el;
              }}
              aria-hidden
              style={{ height: i === 0 ? 0 : PILE_DWELL }}
            />
            <PileCard
              feature={feature}
              index={i}
              geometry={geometry}
              layoutTick={layoutTick}
              scrollY={scrollY}
              reduceMotion={reduceMotion}
            />
          </Fragment>
        ))}
        {/* The last card's dwell, before the pile leaves. */}
        <div aria-hidden style={{ height: PILE_DWELL }} />
      </div>
      <div>
        <PileRail railRef={railRef} active={active} reveal={railReveal} onOpen={onOpen} />
      </div>
    </div>
  );
}

// ── Social proof, posts, the close ──────────────────────────────────────────

function PhoneProof({
  reduceMotion,
  users,
}: {
  reduceMotion: boolean;
  users: number | null;
}) {
  const { t, i18n } = useTranslation();
  const { ref, arrived } = useRise(reduceMotion, 0.3);
  const total = formatCompactMillions(users ?? FALLBACK_USER_COUNT, i18n.language);
  return (
    <section ref={ref} className="flex w-full flex-col items-center">
      <KaSlot id="proof" size={KA_SMALL} />
      <TriggeredLine
        segments={[
          { text: t('landingV3.social.count', { total }), hl: true },
          { text: t('landingV3.social.students') },
        ]}
        arrived={arrived}
        delay={0.1}
        reduceMotion={reduceMotion}
        className={cn('mt-[13px] px-6', PHONE_HEADLINE_CLASS)}
      />
      <div className="mt-6 w-full max-w-[640px] px-4">
        <WorldDotMap
          arrived={arrived}
          reduceMotion={reduceMotion}
          className="h-auto w-full"
        />
      </div>
      <motion.div
        initial={reduceMotion ? false : { opacity: 0 }}
        animate={arrived ? { opacity: 1 } : undefined}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="mt-6 w-full max-w-[640px]">
        <ReviewsRow />
      </motion.div>
    </section>
  );
}

function PhonePosts({ reduceMotion }: { reduceMotion: boolean }) {
  const { t } = useTranslation();
  const { ref, arrived } = useRise(reduceMotion, 0.3);
  return (
    <section ref={ref} className="flex w-full flex-col items-center">
      <KaSlot id="posts" size={KA_SMALL} />
      <TriggeredLine
        segments={[
          { text: t('landingV3.posts.hl'), hl: true },
          { text: t('landingV3.posts.post') },
        ]}
        arrived={arrived}
        delay={0.1}
        reduceMotion={reduceMotion}
        className={cn('mt-[13px] max-w-[548px] px-6', PHONE_HEADLINE_CLASS)}
      />
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 16 }}
        animate={arrived ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.5, delay: 0.3, ease: EASE_OUT }}
        className="mt-8 w-full max-w-[640px]">
        {/* Posters load as their cards come near: only once the block has. */}
        {arrived && <PostsRow />}
      </motion.div>
      <div className="mt-8">
        <TikTokButton />
      </div>
    </section>
  );
}

/**
 * The close (Figma 109:1432): Ka at his desk — the Rive desk Ka, a tap pokes
 * him — the tutor line, the sign-up and the store ratings. The floating Ka
 * hands over to him on the spot where his head is (`deskShown`).
 */
function PhoneClose({
  reduceMotion,
  signUpVariant,
  deskShown,
  onFinalChange,
}: {
  reduceMotion: boolean;
  signUpVariant: 'primary' | 'secondary';
  deskShown: MotionValue<number>;
  /** Its sign-up is on screen: the top bar's steps back (one primary at a
   *  time, as on desktop). */
  onFinalChange: (onScreen: boolean) => void;
}) {
  const { t } = useTranslation();
  const { ref, arrived, rise } = useRise(reduceMotion, 0.3);
  const onScreen = useInView(ref, { amount: 0.3 });
  useEffect(() => onFinalChange(onScreen), [onScreen, onFinalChange]);
  return (
    <section ref={ref} className="flex w-full flex-col items-center px-6 text-center">
      <DeskKa
        shown={deskShown}
        reduceMotion={reduceMotion}
        slotId="final"
        style={{
          width: 'min(320px, 100%)',
          aspectRatio: '4 / 3',
          marginTop: -DESK_KA_HEADROOM,
        }}
      />
      <TriggeredLine
        segments={[
          { text: t('landingV3.finalKa.pre') },
          { text: t('landingV3.finalKa.hl'), hl: true },
          { text: t('landingV3.finalKa.post') },
        ]}
        arrived={arrived}
        delay={0.2}
        reduceMotion={reduceMotion}
        className={cn('mt-8 max-w-[560px]', PHONE_HEADLINE_CLASS)}
      />
      <motion.div {...rise} className="mt-10 flex w-full flex-col items-center gap-4">
        <div className="w-full max-w-[336px]">
          <GetStartedCta
            placement="final"
            size="xxl"
            variant={signUpVariant}
            fullWidth
            leftIcon={WandIcon}>
            {t('landingV3.cta')}
          </GetStartedCta>
        </div>
        <StoreRatingPills />
      </motion.div>
    </section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PhoneLanding({
  reduceMotion,
  users,
}: {
  reduceMotion: boolean;
  users: number | null;
}) {
  // Where the device has a store, the store is the page's one primary (the
  // floating button) and signing up is secondary (user 2026-09-28).
  const storeDevice = useStoreDevice();
  const signUpVariant = storeDevice ? 'secondary' : 'primary';
  const { scrollY } = useScroll();
  // The desktop bar's gate: away while the reader races back up the page.
  const travelling = useScrollTravel(scrollY, reduceMotion);

  const stageRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const box = useHeroBox(stageRef);
  const pile = useRef<PileGeometry>(NO_PILE);
  const rail = useRef<RailGeometry>(NO_RAIL);
  const layoutTick = useMotionValue(0);
  const firstCardTop = useRef(0);
  useMotionValueEvent(layoutTick, 'change', () => {
    firstCardTop.current = pile.current.tops[0] ?? 0;
  });
  const hero = useHeroTimeline({ scrollY, box, cardTop: firstCardTop, layoutTick });
  // The rail's own chips take over as the hero's land (reduced motion: once
  // the card has brought the rail up, with no flight before it).
  const railReveal = useTransform(() => {
    const o = hero.organize.get();
    return reduceMotion ? clamp01((o - 0.5) / 0.3) : clamp01((o - 0.94) / 0.06);
  });

  // The close's own sign-up is on screen: the floating one (on a computer)
  // steps aside, one primary at a time.
  const [final, setFinal] = useState(false);
  // Ka in his corner puts the floating button beside him — only where the
  // screen is a phone's: wider, the corner is far from the centred button.
  const [kaInCorner, setKaInCorner] = useState(true);
  const kaBeside = kaInCorner && box.w < 480;
  // The close's desk Ka, shown when the floating Ka hands over to him.
  const deskShown = useMotionValue(0);

  // The chips, the rail, the Study Tools menu and the footer land on a card
  // at rest on top of the pile.
  const openArticle = useCallback(
    (i: number) =>
      window.scrollTo({
        top: pileRestAt(pile.current, i),
        behavior: reduceMotion ? 'auto' : 'smooth',
      }),
    [reduceMotion],
  );
  // Arriving from About's Study Tools or footer: straight onto the card.
  const arriveAt = useCallback(
    (i: number) => window.scrollTo({ top: pileRestAt(pile.current, i) }),
    [],
  );
  useLandingCardArrival(arriveAt);
  const toTop = useCallback(
    () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }),
    [reduceMotion],
  );

  return (
    <div className="lv3-phone relative isolate min-h-screen w-full bg-background">
      <PageLattice />
      <PhoneTopBar
        handoff={hero.handoff}
        travelling={travelling}
        reduceMotion={reduceMotion}
        onJumpTo={openArticle}
        onLogoClick={toTop}
      />
      {/* Its own layer, so Ka can pass under it from the map on (PhoneKa). */}
      <main
        ref={mainRef}
        className="relative z-[4] flex w-full flex-col items-center gap-24 pb-24">
        {/* The pinned hero, and the pile pulled up under it (no gap between
            them: the first card rises over the hero). */}
        <div className="flex w-full flex-col items-center">
          <PhoneHeroStage
            stageRef={stageRef}
            box={box}
            heroOut={hero.heroOut}
            lineOpacity={hero.lineOpacity}
            lineFired={hero.lineFired}
            users={users}
            reduceMotion={reduceMotion}
          />
          <PhonePile
            reduceMotion={reduceMotion}
            scrollY={scrollY}
            geometry={pile}
            rail={rail}
            layoutTick={layoutTick}
            railReveal={railReveal}
            onOpen={openArticle}
          />
        </div>
        {/* The agent chat (Figma 119:8431): Ka floats into its card. */}
        <div {...sectionAnchor('agent')} className="w-full max-w-[640px] px-4">
          <AgentChat layout="stacked" reduceMotion={reduceMotion} />
        </div>
        {/* The tiles 50px under the chat, as on desktop (user 2026-09-30). */}
        <div className="-mt-[46px] w-full max-w-[640px] px-4">
          <TilesSection layout="stacked" />
        </div>
        <PhoneProof reduceMotion={reduceMotion} users={users} />
        <PhonePosts reduceMotion={reduceMotion} />
        <PhoneClose
          reduceMotion={reduceMotion}
          signUpVariant={signUpVariant}
          deskShown={deskShown}
          onFinalChange={setFinal}
        />
        <div className="w-full max-w-[640px] px-4">
          {/* The FAQ sheet without its glass: no backdrop blur on the large
              surfaces here. */}
          <FaqSection className="rounded-[40px] bg-secondary-50/90 px-4 backdrop-blur-none" />
        </div>
      </main>
      <LandingFooter onJumpTo={openArticle} />
      <PhoneChipLayer
        box={box}
        organize={hero.organize}
        rail={rail}
        scrollY={scrollY}
        layoutTick={layoutTick}
        atRest={hero.atRest}
        reduceMotion={reduceMotion}
        onOpen={openArticle}
      />
      <PhoneKa
        contentRef={mainRef}
        lineSpot={hero.kaCentred ? heroKaSpot(box) : null}
        scrollY={scrollY}
        layoutTick={layoutTick}
        deskShown={deskShown}
        reduceMotion={reduceMotion}
        onCornerChange={setKaInCorner}
      />
      {storeDevice ? (
        <FloatingGetApp store={storeDevice} kaBeside={kaBeside} />
      ) : (
        <FloatingSignUp kaBeside={kaBeside} hidden={final} />
      )}
    </div>
  );
}
